import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MCPConfig } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { BotService } from './bot.service';

/**
 * Verwaltet Miro-Boards und die Miro-Kontoverknüpfung (`RAGAI.MIRO`).
 * @remarks
 * Bereits die Instanziierung (nicht ein expliziter Methodenaufruf!) hat einen Nebeneffekt: Der Konstruktor prüft,
 * ob der Bot ein aktiviertes Miro-MCP-Tool hat und der eingeloggte Nutzer noch nicht per Miro autorisiert ist
 * (`RAGAI.loggedInUser.miroAuth`). Ist das der Fall, wird das **oberste Fenster automatisch** zum
 * Miro-OAuth-Install-Flow weitergeleitet - ganz ohne RAGAI-Methodenaufruf. {@link copyMiroBoard}/
 * {@link updateMiroBoard} setzen dagegen eine bereits bestehende Autorisierung voraus.
 */
@Injectable({
  providedIn: 'root'
})
export class MiroService {

  private http = inject(HttpClient);
  private botService = inject(BotService);

  constructor() {
    void firstValueFrom(this.http.get<MCPConfig[]>("/api/user/mcp/getmanaged/" + this.botService.botConfig?._id)).then((v: MCPConfig[]) => {

      const hasMiroMCP = v.some(mcp => mcp.type === "managed" && mcp.toolName === "miro");
      if (hasMiroMCP) {
        if (!window.RAGAI.loggedInUser?.miroAuth) {
          if (window.top) {
            const redirect_uri = encodeURIComponent(window.top.location.origin + '/' + window.top.location.pathname.split("/")[1] + "/auth/miro");
            const state = btoa(encodeURIComponent(window.top.location.pathname.substring(3).split("/").filter(s => s != "").join("/")));
            const href = `https://miro.com/app-install/?response_type=code&client_id=3458764664985448357&redirect_uri=${redirect_uri}&state=${state}`;
            window.top.location.href = href;
          }
        }
      }
    });
  }

  /**
   * Kopiert ein bestehendes Miro-Board als Vorlage für ein neues Board.
   * @param copy_from ID des zu kopierenden Quell-Boards.
   * @param options Optionen für das neue Board.
   * @param options.description Beschreibung des neuen Boards.
   * @param options.name Name des neuen Boards.
   * @param options.policy Freigabe-/Zugriffsrichtlinie des neuen Boards.
   * @param options.teamId ID des Teams, dem das Board zugeordnet wird.
   * @returns Die Antwort der Miro-API.
   */
  public async copyMiroBoard(copy_from: string, options?: {
    description?: string;
    name?: string;
    policy?: unknown;
    teamId?: string;
  }) {
    return await firstValueFrom(this.http.put("/api/user/miro/copyboard", { copy_from, options }));
  }

  /**
   * Benennt ein bestehendes Miro-Board um.
   * @param boardId ID des Boards.
   * @param name Der neue Name.
   * @returns Status der Änderung.
   */
  public async updateMiroBoard(boardId: string, name: string) {
    return await firstValueFrom(this.http.patch<{ status: "ok" }>("/api/user/miro/updateboard/" + boardId, { name }));
  }


}
